import { BaseModal, Button, FileUploader, Input, Switch } from '@cokitchen/cokitchen-components';
import React, { useEffect, useState, useMemo } from 'react';
import './styles.scss';
import { STORAGE_KEYS } from 'utils/constants';
import useEventDetailsContext from 'contexts/event-details.context';
import DateRange from 'components/date-range';
import { CalendarAltIcon } from 'cokitchen-icons';
import moment from 'moment';
import LocalTextArea from 'components/base/local-text-area';
import TimeSelector from 'components/base/time-selector';
import { TimeSelectorType } from 'components/base/time-selector/types';
import { changeDateTime } from 'utils/functions';

type Range = {
  startDate?: Date;
  endDate?: Date;
  key?: string;
};
const EventDetails = () => {
  const { formData, setFormData, setDisabled } = useEventDetailsContext();

  const [showDate, setShowDate] = useState(false);
  const rangeKey = 'selection';
  const [state, setState] = useState<Range>({
    startDate: new Date(),
    endDate: new Date(),
    key: rangeKey
  });
  const [isDateRange, setIsDateRange] = useState<boolean>(false);
  const [startTime, setStartTime] = useState({} as TimeSelectorType);
  const [endTime, setEndTime] = useState({} as TimeSelectorType);

  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) as string;

  useEffect(() => {
    setDisabled(!formData.name || !formData.start_date || !formData.event_picture);
  }, [formData]);

  useEffect(() => {
    if (!isDateRange && !formData.end_date.length) {
      setFormData({ ...formData, end_date: formData.start_date });
    }
  }, [isDateRange, formData.end_date]);

  useEffect(() => {
    if (isDateRange) {
      setState({
        ...state,
        startDate: state.startDate,
        endDate: moment(state.startDate).add(1, 'days').toDate()
      });
    } else {
      setState({
        ...state,
        startDate: state.startDate,
        endDate: state.startDate
      });
    }
  }, [isDateRange]);

  const handleSelectDate = () => {
    if (state.startDate && state.endDate) {
      const newStartDate = new Date(state.startDate);

      const newEndDate = new Date(state.endDate);

      setFormData({
        ...formData,
        start_date: changeDateTime(newStartDate.toISOString(), startTime),
        end_date: changeDateTime(newEndDate.toISOString(), endTime)
      });
    }
  };

  useEffect(() => {
    if (formData.start_date.length > 0 && formData.end_date.length > 0) {
      setFormData({
        ...formData,
        start_date: changeDateTime(formData.start_date, startTime),
        end_date: changeDateTime(formData.end_date, endTime)
      });
    }
  }, [startTime, endTime]);
  const title = useMemo(() => {
    const { start_date, end_date } = formData;
    if (!start_date) return 'Select date';
    const isSameDate = start_date.split('T')[0] === end_date.split('T')[0];
    const format = 'DD/MM/YYYY, h:mm a';

    if (isSameDate) {
      return `${moment(start_date).format(format)} - ${moment(end_date).format('h:mm a')}`;
    } else {
      return `${moment(start_date).format(format)} - ${moment(end_date).format(format)}`;
    }
  }, [formData.start_date, formData.end_date]);
  return (
    <div className='event-form'>
      <p className='text-[32px] font-semibold text-[#2E3031] mb-1'>Provide event details</p>
      <p className='text-[#6F7376] mb-8'>
        Brief details of your event will enable your guests join the fun!
      </p>
      <div className='flex flex-col gap-7'>
        <Input
          design='bordered'
          type='text'
          label='Event name'
          placeholder='Donatus Globus ltd'
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        />
        <div>
          <p className='mb-2'>Event date</p>
          <div
            className='cursor-pointer flex justify-between w-full bg-[#FAFAFA] border-b-2 border-b-[#D5D6D7] h-[52px] items-center px-[14px] text-[#B5B8BA]'
            onClick={() => setShowDate(true)}>
            <p className={`${formData.start_date ? 'text-[#2E3031]' : 'text-[#9BA3AF]'}`}>
              {title}
            </p>
            <CalendarAltIcon />
          </div>
        </div>
        {showDate && (
          <BaseModal
            isOpen={showDate}
            onRequestClose={() => setShowDate(false)}
            title='Select Date'>
            <div className='w-full daterange'>
              <div>
                <DateRange
                  editableDateInputs={true}
                  onChange={(item) => {
                    setState(item[rangeKey]);
                  }}
                  moveRangeOnFirstSelection={!isDateRange}
                  ranges={isDateRange ? [state] : [{ ...state, endDate: state.startDate }]}
                  dateDisplayFormat='dd/MM/yyyy'
                  className='range'
                  showDateDisplay={isDateRange}
                  showPreview={isDateRange}
                  minDate={new Date()}
                  maxDate={new Date(new Date().getFullYear() + 5, 11, 31)}
                />
              </div>
              <div className='w-[80%] mx-auto'>
                <p className='mb-1 text-[#2E3031] text-sm font-medium mt-2'>Time</p>
                <div className='flex items-center gap-2 w-full justify-between rounded pt-3 pb-4'>
                  <TimeSelector
                    value={startTime}
                    onChange={(k) => {
                      setStartTime(k);
                    }}
                  />

                  <span className='text-gray-5 text-xs leading-[19px] mt-2 mb-1'>--</span>
                  <TimeSelector
                    value={endTime}
                    onChange={(k) => {
                      setEndTime(k);
                    }}
                  />
                </div>
              </div>
              <div className='flex justify-between w-full mt-6 items-center button_box'>
                <Switch
                  label='End Date'
                  labelPosition='left'
                  active={isDateRange}
                  onChange={(checked) => {
                    setIsDateRange(checked);
                  }}
                  switchClassName='bg-[#e60a2b]'
                  small
                />
                <div className='flex gap-3'>
                  <Button
                    variant='block'
                    color='gray'
                    className='w-[90px] h-[42px] text-sm gray_btn'
                    onClick={() => setShowDate(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant='block'
                    color='primary'
                    className='w-[90px] h-[42px] text-sm'
                    onClick={() => {
                      setShowDate(false);
                      handleSelectDate();
                    }}>
                    Done
                  </Button>
                </div>
              </div>
            </div>
          </BaseModal>
        )}

        <div className='flex flex-col gap-2'>
          <p>
            Event description&nbsp;<span className='font-normal text-gray-3'>(optional)</span>
          </p>
          <LocalTextArea
            rows={4}
            placeholder='What do you like about FoodCourt Business'
            value={formData.description}
            onChange={(e: any) => {
              setFormData({ ...formData, description: e.target.value });
            }}
          />
        </div>

        <FileUploader
          token={token}
          label='Add a picture'
          className='mb-6 border-none p-0 flex flex-col space-y-1'
          fileUrls={formData.event_picture ? [formData.event_picture] : []}
          setFileUrls={(img) => {
            setFormData({
              ...formData,
              event_picture: img[0]
            });
          }}
        />
      </div>
    </div>
  );
};

export default EventDetails;
